import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Calendar, MapPin, Clock, Ruler, Loader2 } from "lucide-react";
import { Meta, buildTitle, buildDescription } from "../components/SEO";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { CtaStrip } from "../components/Sections";
import { SmartImage } from "../components/SmartImage";
import { LiteVideoEmbed } from "../components/LiteVideoEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function imgUrl(img) {
  if (!img) return undefined;
  const u = typeof img === "string" ? img : img.url;
  return u || undefined;
}

function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/projects`);
        if (res.ok) {
          const data = await res.json();
          setProjects(data.projects || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return { projects, loading };
}

export default function ProjectenPage() {
  const { projects, loading } = useProjects();

  return (
    <>
      <Meta
        title="Projecten — Renovatie & verbouwing Zuid-Holland | Koning"
        description="Bekijk recente renovatie- en verbouwprojecten in Zuid-Holland, met voor- en na-foto's van badkamers, zolders en complete woningen. Laat u inspireren."
      />
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 border-b border-koning-border" data-testid="projecten-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="eyebrow mb-5">Portfolio</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-[#1A1816] tracking-tight max-w-4xl">
            Projecten die <span className="italic text-koning-gold">spreken</span>.
          </h1>
          <p className="mt-6 text-[#4A453E] max-w-2xl leading-relaxed">
            Een selectie van onze meest recente realisaties — van complete renovaties tot maatwerk badkamers en aanbouwen. Sleep door voor- en na-foto&apos;s om het verschil te ervaren.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20" data-testid="projecten-grid">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="text-koning-gold animate-spin" size={28} />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20 text-koning-muted">Binnenkort meer projecten beschikbaar.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {projects.map((p, i) => (
                <article key={p.slug} className={`${i % 3 === 0 ? "md:col-span-2" : ""}`} data-testid={`project-${p.slug}`}>
                  <Link to={`/projecten/${p.slug}`} className="block group">
                    <div className="img-zoom relative aspect-[16/10] mb-5 overflow-hidden border border-koning-border">
                      <SmartImage src={imgUrl(p.cover)} alt={p.title} aspect="16/10" sizes="(max-width: 768px) 100vw, 50vw" eager={i === 0} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs tracking-[0.22em] uppercase text-koning-muted mb-3">
                      <span className="text-koning-gold">{p.category}</span>
                      <span>·</span>
                      <span>{p.city}</span>
                      {p.year && <><span>·</span><span>{p.year}</span></>}
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl text-[#1A1816] group-hover:text-koning-gold transition-colors">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-[#4A453E] text-sm md:text-base leading-relaxed max-w-2xl">{p.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-koning-gold text-xs tracking-[0.22em] uppercase">
                      Lees verhaal <ArrowUpRight size={14} />
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaStrip title="Uw project als volgende?" subtitle="Vraag een vrijblijvende offerte aan. Wij bouwen het samen met u op." />
    </>
  );
}

export function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [otherProjects, setOtherProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/projects/${slug}`);
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        const data = await res.json();
        setProject(data);
        // Fetch others (excluding current)
        const allRes = await fetch(`${API}/projects?limit=10`);
        if (allRes.ok) {
          const all = await allRes.json();
          setOtherProjects((all.projects || []).filter((p) => p.slug !== slug).slice(0, 3));
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="text-koning-gold animate-spin" size={28} />
      </div>
    );
  }
  if (notFound || !project) return <Navigate to="/projecten" replace />;
  const hasBeforeAfter = Boolean(project.before?.url && project.after?.url);

  return (
    <>
      <Meta title={buildTitle(project.title)} description={buildDescription(project.excerpt)} />

      <section className={`relative ${hasBeforeAfter ? "min-h-[46vh]" : "min-h-[75vh]"} flex items-end overflow-hidden grain`} data-testid="project-hero">
        <SmartImage src={imgUrl(project.cover)} alt={project.title} sizes="100vw" eager className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-16 md:py-24">
          <div className="text-xs tracking-[0.28em] uppercase text-koning-gold mb-4">
            <Link to="/projecten" className="hover:underline">Projecten</Link> · {project.category}
          </div>
          <h1 className="font-display text-white text-4xl md:text-6xl lg:text-7xl tracking-tight max-w-4xl">{project.title}</h1>
          <p className="mt-6 text-koning-text-2/90 max-w-2xl leading-relaxed">{project.excerpt}</p>
        </div>
      </section>

      {hasBeforeAfter && (
        <section className="bg-[#0B0B0B] border-b border-koning-border" data-testid="project-before-after-top">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 py-8 md:py-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
              <div>
                <div className="eyebrow mb-2">Vóór & ná</div>
                <h2 className="font-display text-2xl md:text-4xl text-white tracking-tight">Sleep en zie de transformatie</h2>
              </div>
              <p className="text-koning-muted text-sm max-w-md">Links de uitgangssituatie, rechts het resultaat na de {project.category?.toLowerCase() || "renovatie"} in {project.city}.</p>
            </div>
            <BeforeAfterSlider
              before={imgUrl(project.before)}
              after={imgUrl(project.after)}
              beforeAlt={project.before?.alt || `Vóór de ${project.category?.toLowerCase() || "renovatie"} — ${project.title}`}
              afterAlt={project.after?.alt || `Ná de ${project.category?.toLowerCase() || "renovatie"} — ${project.title}`}
              aspect="16/9"
              eager
            />
          </div>
        </section>
      )}

      <section className="py-12 border-b border-koning-border">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {project.year && <Detail icon={Calendar} label="Jaar" value={project.year} />}
          {project.city && <Detail icon={MapPin} label="Locatie" value={project.city} />}
          {project.duration && <Detail icon={Clock} label="Doorlooptijd" value={project.duration} />}
          {project.surface && <Detail icon={Ruler} label="Oppervlakte" value={project.surface} />}
        </div>
      </section>


      {project.video_url && (
        <section className="py-20 md:py-28 bg-koning-surface border-y border-koning-border" data-testid="project-video">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="eyebrow mb-4">Videotour</div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight mb-10">Zie het resultaat in beweging</h2>
            <LiteVideoEmbed
              url={project.video_url}
              title={project.title}
              posterFallback={imgUrl(project.cover)}
            />
          </div>
        </section>
      )}

      {project.gallery?.length > 0 && (
        <section className="py-12 bg-koning-surface border-y border-koning-border">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            {project.gallery.map((g, i) => (
              <div key={typeof g === "string" ? g : g?.url || i} className="img-zoom relative aspect-[4/5] border border-koning-border">
                <SmartImage src={imgUrl(g)} alt={g?.alt || project._gallery?.[i]?.alt || `${project.title} — ${project.category} ${project.city}, foto ${i + 1}`} sizes="(max-width: 768px) 100vw, 33vw" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      <CtaStrip title="Verleid om iets soortgelijks te realiseren?" subtitle="Vraag een vrijblijvende prijsindicatie aan — u ontvangt binnen één werkdag persoonlijk contact." />

      {otherProjects.length > 0 && (
        <section className="py-20 border-t border-koning-border" data-testid="other-projects">
          <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-display text-2xl md:text-4xl text-[#1A1816] tracking-tight">Andere projecten</h2>
              <Link to="/projecten" className="btn-outline">Alle projecten <ArrowUpRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherProjects.map((p) => (
                <Link key={p.slug} to={`/projecten/${p.slug}`} className="group img-zoom block border border-koning-border">
                  <div className="relative aspect-[4/5]">
                    <img src={imgUrl(p.cover)} alt={p.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-koning-bg/95 via-koning-bg/30 to-transparent" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase">{p.city}</div>
                      <h3 className="font-display text-white text-xl mt-2">{p.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

const Detail = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4">
    <div className="w-11 h-11 border border-koning-border flex items-center justify-center text-koning-gold"><Icon size={18} /></div>
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted">{label}</div>
      <div className="text-white font-display text-xl mt-0.5">{value}</div>
    </div>
  </div>
);
